/**
 * Channel IO for `useAiDraftWriter` (EAI-1006).
 *
 * The hook owns the *policy* of an AI draft — what refine means, version
 * bookkeeping, restore-on-empty — and knows nothing about how a given composer
 * stores its content. Each composer supplies an adapter with these five calls:
 *
 *   getElement()         → optional; the composer's DOM node, for watching it
 *   readDraft()          → the user's current content, as plain text (for the API)
 *   readSnapshot()       → optional; the same content in restorable form. Only
 *                          needed where plain text loses something — the rich
 *                          email editor returns HTML here. Falls back to
 *                          readDraft when absent.
 *   readSubject()        → optional; the composer's current subject line
 *   writeSubject(text)   → optional; apply a subject (result, or Undo)
 *   clearDraft()         → blank the composer while the request is in flight
 *   restoreDraft(text)   → put content back (failed call, or Undo)
 *   writeDraft(text)     → render a result; resolves when fully written
 *   extractResult(res)   → pull the content out of a getAiResponse payload
 *
 * Adapters are plain factories, not hooks, so any component that can reach the
 * composer can build one — InputArea builds the email adapter for Undo, and
 * EmailTextBox builds the same one for its Refine button.
 */

import { setFieldAndValidate } from "../../../../library/antdHelpers";

const TYPE_DELAY_MS = 20;

/**
 * Plain `<textarea>` composers: the Text channel and the review reply box.
 * Results are typed in a character at a time, which is what makes an AI draft
 * feel like it is being written rather than pasted.
 */
export const createTextareaAdapter = ({ text, setInputText, placeholder }) => ({
  readDraft: () => text?.current?.value || "",

  clearDraft: () => {
    if (!text?.current) return;
    setInputText?.("");
    text.current.placeholder = placeholder || "Generating response...";
    text.current.value = "";
    text.current.style.height = "0px";
    text.current.focus();
  },

  restoreDraft: content => {
    if (!text?.current) {
      setInputText?.(content);
      return;
    }
    text.current.value = content;
    text.current.style.height = `${text.current.scrollHeight}px`;
    setInputText?.(content);
  },

  extractResult: res => res?.content || "",

  writeDraft: content =>
    new Promise(resolve => {
      if (!text?.current) {
        setInputText?.(content);
        resolve();
        return;
      }
      let index = 0;
      const typeCharacter = () => {
        // The composer can unmount mid-animation (lead switch) — stop rather
        // than throwing on a detached ref.
        if (!text?.current) {
          resolve();
          return;
        }
        text.current.style.height = `${text.current.scrollHeight}px`;
        text.current.value =
          index === 0 ? content.charAt(0) : text.current.value + content.charAt(index);
        index += 1;
        if (index >= content.length) {
          setInputText?.(content);
          resolve();
          return;
        }
        setTimeout(typeCharacter, TYPE_DELAY_MS);
      };
      typeCharacter();
    }),
});

// The quoted thread on a reply lives in a #reply-info block inside the same
// editor. Everything from its top-level wrapper down is the previous
// conversation, not the user's draft: it is never sent to the AI and never
// overwritten by a result.
const findQuotedBlock = root => {
  const replyInfo = root?.querySelector?.("#reply-info");
  if (!replyInfo) return null;
  let block = replyInfo;
  while (block.parentNode && block.parentNode !== root) block = block.parentNode;
  return block;
};

/**
 * The rich-text email composer.
 *
 * Content is HTML in a contenteditable, so there is no typing animation — a
 * result is inserted in one go, above the quoted thread if there is one.
 *
 * @param {{
 *   emailTextRef?: object, emailMode?: string, emailForm?: object,
 *   onSubjectChange?: (subject: string) => void,
 * }} config
 *   `emailMode === "select"` is the standalone compose box (emailTextRef);
 *   anything else is the reply editor found by id. The default matches
 *   EmailTextBox's own default, so callers that don't know the mode still
 *   resolve to the same editor it does.
 */
export const createEmailAdapter = ({
  emailTextRef,
  emailMode = "select",
  emailForm,
  onSubjectChange,
} = {}) => {
  const getEditor = () => {
    if (emailMode === "select") return emailTextRef?.current || null;
    return document.getElementById("email-text-box-container")?.querySelector("#rich-text") || null;
  };

  // The user's own draft with the quoted thread stripped, as a detached clone.
  //
  // On a reply the draft is NOT a sibling of the quoted block: it sits inside
  // the same top-level block, immediately before #reply-info (which is why
  // writeDraft clears "everything before #reply-info" rather than the block).
  // So the quote is stripped from #reply-info onward, not by dropping the
  // wrapper — dropping it takes the draft with it and makes an obviously
  // non-empty composer read as empty.
  const draftClone = () => {
    const editor = getEditor();
    if (!editor) return null;
    const clone = editor.cloneNode(true);
    const replyInfo = clone.querySelector("#reply-info");

    if (replyInfo) {
      let block = replyInfo;
      while (block.parentNode && block.parentNode !== clone) block = block.parentNode;
      // #reply-info and everything after it inside the wrapper.
      let node = replyInfo;
      while (node) {
        const next = node.nextSibling;
        node.remove();
        node = next;
      }
      // Any blocks that follow the wrapper.
      let sibling = block.nextSibling;
      while (sibling) {
        const next = sibling.nextSibling;
        sibling.remove();
        sibling = next;
      }
    }

    return clone;
  };

  // Plain text — this is what refine sends as the draft, where markup would only
  // be noise.
  const readDraft = () => (draftClone()?.textContent || "").trim();

  // The same content as markup. Undo restores this, not readDraft's text —
  // otherwise stepping back would flatten the user's bold, spacing and line
  // breaks into one plain paragraph.
  const readSnapshot = () => draftClone()?.innerHTML || "";

  const writeDraft = content =>
    new Promise(resolve => {
      const editor = getEditor();
      if (!editor) {
        resolve();
        return;
      }
      const quoted = findQuotedBlock(editor);
      if (!quoted) {
        editor.innerHTML = content;
        editor.focus();
        resolve();
        return;
      }

      // Rebuild as [result] + [quoted thread], dropping whatever the user's
      // draft was. Anything sitting before #reply-info inside the quoted
      // wrapper is leftover draft too, so it goes.
      editor.innerHTML = "";
      editor.appendChild(quoted);
      const replyInfo = quoted.querySelector("#reply-info");
      let beforeReplyInfo = true;
      Array.from(quoted.childNodes).forEach(node => {
        if (node === replyInfo) beforeReplyInfo = false;
        if (beforeReplyInfo) quoted.removeChild(node);
      });

      const holder = document.createElement("div");
      holder.innerHTML = content;
      const fragment = document.createDocumentFragment();
      while (holder.firstChild) fragment.appendChild(holder.firstChild);
      editor.insertBefore(fragment, quoted);
      editor.focus();
      resolve();
    });

  return {
    // Lets useDraftPresence watch the editor for writes it can't otherwise see.
    getElement: getEditor,
    readDraft,
    readSnapshot,
    readSubject: () => emailForm?.getFieldValue?.("subject") || "",
    // getAiResponse refines the subject alongside the body, so a refined email
    // applies both. The form holds the value that gets sent; the redux mirror is
    // what manual typing updates, so both are kept in step.
    writeSubject: subject => {
      if (subject === undefined || subject === null) return;
      // Validate as well as set: a "Subject is required" error raised by an
      // earlier Send would otherwise stay on screen next to the subject the AI
      // just supplied. See setFieldAndValidate.
      setFieldAndValidate(emailForm, "subject", subject);
      onSubjectChange?.(subject);
    },
    // Nothing to blank: the email editor shows no placeholder, and wiping it
    // would throw away the quoted thread before we know the call succeeded.
    clearDraft: () => {},
    // Undo and failed calls hand back a readSnapshot HTML string.
    restoreDraft: content => writeDraft(content),
    writeDraft,
    extractResult: res => res?.bodyHtml || res?.body || res?.content || "",
  };
};
